import { useState } from "react";
import { api } from "../api/staff";
import { useStaffMe } from "../context/StaffMeContext";

interface UserResult {
  bypassApplied: boolean;
  status: string;
  subscription: {
    planCode: string;
    status: string;
  } | null;
  profile: {
    user: {
      publicId: string;
      displayName: string | null;
      username: string | null;
      description: string | null;
      flags: number;
      createdAt: string;
      avatarUrl: string | null;
      accentPrimary: string | null;
      accentSecondary: string | null;
    };
  };
  privateId: string | null;
  publicKey: string;
  totpEnabled: boolean;
  savedSearchesCount: number;
  connectedDevicesCount: number;
  credits: {
    plan: {
      code: string;
      dailyCredits: number | null;
    };
    used: number;
    limit: number | null;
    remaining: number | null;
    usagePercent: number;
    resetIn: string | null;
    overage: {
      enabled: boolean;
    };
  };
  wallet?: {
    balanceMinor: string;
    balanceFormatted: string;
    currency: string;
    totalDepositedMinor: string;
    totalDepositedFormatted: string;
    totalSpentMinor: string;
    totalSpentFormatted: string;
    transactionsCount: number;
  };
  telegram?: {
    id: string;
    username: string | null;
    chatId: string | null;
    linkedAt: string | null;
    avatarUrl?: string | null;
  } | null;
  monthlyUsage: Record<string, { osint: number; csint: number; blockchain: number; total: number }>;
  subscriptionsList: {
    id: string;
    planCode: string;
    status: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    createdAt: string;
  }[];
}

const CREAM = "#fbe7b5";
const CREAM_DIM = "#b89c6a";

type CreditPlan = "pro" | "max";
type Tier = "advanced" | "pro" | "pro_plus" | "max";


const TIER_OPTIONS: { value: Tier; label: string }[] = [
  { value: "advanced", label: "Advanced" },
  { value: "pro", label: "Pro" },
  { value: "pro_plus", label: "Pro+" },
  { value: "max", label: "Max" },
];

const DURATION_OPTIONS = [
  { days: 7, label: "7 jours" },
  { days: 30, label: "30 jours" },
  { days: 90, label: "90 jours" },
  { days: 180, label: "180 jours" },
  { days: 365, label: "1 an" },
];

function Avatar({ avatarUrl, name, accent }: { avatarUrl: string | null; name: string | null; accent: string }) {
  const initial = ((name && name[0]) ?? "?").toUpperCase();
  return (
    <div style={{
      width: 48, height: 48, borderRadius: 12, overflow: "hidden", flexShrink: 0,
      background: accent,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {avatarUrl ? (
        <img src={avatarUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <span style={{ fontSize: 18, fontWeight: 800, color: "rgba(14,16,22,0.85)" }}>{initial}</span>
      )}
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 11, color: "rgba(7,10,13,0.4)", minWidth: 100 }}>{label}</span>
      <span style={{ fontSize: 11, fontWeight: 500, color: "#070a0d", fontFamily: mono ? "monospace" : undefined }}>
        {value}
      </span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{
      marginTop: 16,
      background: "rgba(255,255,255,0.82)",
      border: "1px solid #fff",
      borderRadius: 16,
      backdropFilter: "blur(10px) saturate(145%)",
      boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
      padding: 20,
    }}>
      <p style={{ margin: "0 0 12px", fontSize: 13, fontWeight: 700, color: "#070a0d" }}>{title}</p>
      {children}
    </div>
  );
}

export function UserSearch() {
  const me = useStaffMe();
  const canBypass = me.role === "developer" || me.role === "admin";

  const [identifier, setIdentifier] = useState("");
  const [reason, setReason] = useState("");
  const [result, setResult] = useState<UserResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ban state
  const [banLoading, setBanLoading] = useState(false);
  const [banMsg, setBanMsg] = useState<string | null>(null);
  const [banReason, setBanReason] = useState("");

  // Grant state
  const [grantTier, setGrantTier] = useState<Tier>("pro");
  const [grantDays, setGrantDays] = useState(30);
  const [grantReason, setGrantReason] = useState("");
  const [grantLoading, setGrantLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState<string | null>(null);
  const [grantMsg, setGrantMsg] = useState<string | null>(null);

  async function search() {
    const id = identifier.trim();
    if (!id) return;
    if (canBypass) {
      const r = reason.trim();
      if (!r) {
        setError("Raison requise pour contourner la confidentialité.");
        return;
      }
      if (r.length < 3) {
        setError("La raison doit faire au moins 3 caractères.");
        return;
      }
    }
    setLoading(true);
    setError(null);
    setResult(null);
    setBanMsg(null);
    setGrantMsg(null);
    try {
      const params = canBypass ? `?reason=${encodeURIComponent(reason.trim())}` : "";
      const data = await api<UserResult>(`/staff/admin/users/${id}${params}`);
      setResult(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Erreur inconnue";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function handleBan(newStatus: "active" | "suspended") {
    if (!result) return;
    const id = result.profile.user.publicId;
    const r = banReason.trim();
    if (!r || r.length < 3) { setBanMsg("Raison requise (min. 3 caractères)"); return; }
    setBanLoading(true);
    setBanMsg(null);
    try {
      await api(`/staff/admin/users/${id}/ban`, {
        method: "POST",
        body: JSON.stringify({ status: newStatus, reason: r }),
      });
      setBanMsg(`✓ Utilisateur ${newStatus === "suspended" ? "banni" : "débanni"} avec succès`);
      setResult(prev => prev ? { ...prev, status: newStatus } : null);
    } catch (err: unknown) {
      setBanMsg(err instanceof Error ? err.message : "Erreur");
    } finally {
      setBanLoading(false);
    }
  }


  async function handleSendReset() {
    if (!result?.profile.user.publicId || !resetEmail.trim()) return;
    setResetLoading(true);
    setResetMsg(null);
    try {
      await api(`/staff/admin/users/${result.profile.user.publicId}/send-reset-link`, {
        method: "POST",
        body: JSON.stringify({ email: resetEmail }),
      });
      setResetMsg("✓ Email de réinitialisation envoyé !");
      setResetEmail("");
    } catch (err: any) {
      setResetMsg(`Erreur: ${err.message || "Impossible d'envoyer le mail"}`);
    } finally {
      setResetLoading(false);
    }
  }

  async function handleGrant() {
    if (!result) return;
    const id = result.profile.user.publicId;
    if (!grantReason.trim() || grantReason.trim().length < 3) { setGrantMsg("Raison requise (min. 3 caractères)"); return; }
    setGrantLoading(true);
    setGrantMsg(null);
    try {
      const derivedPlan = grantTier === "max" ? "max" : "pro";
      const data = await api<{ outcome: { subscriptionId: string; currentPeriodEnd: string } }>(
        `/staff/admin/users/${id}/grant-subscription`,
        { method: "POST", body: JSON.stringify({ creditPlan: derivedPlan, tier: grantTier, durationDays: grantDays, reason: grantReason }) },
      );
      setGrantMsg(
        `✓ Abonnement accordé — ID ${data.outcome.subscriptionId} — expire le ${new Date(data.outcome.currentPeriodEnd).toLocaleDateString("fr-FR")}`,
      );
    } catch (err: unknown) {
      setGrantMsg(err instanceof Error ? err.message : "Erreur");
    } finally {
      setGrantLoading(false);
    }
  }

  const accent = result?.profile.user.accentPrimary
    ? `linear-gradient(135deg, ${result.profile.user.accentPrimary}, ${result.profile.user.accentSecondary ?? CREAM_DIM})`
    : `linear-gradient(135deg, ${CREAM}, ${CREAM_DIM})`;

  const memberSince = result
    ? new Date(result.profile.user.createdAt).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
    : null;

  return (
    <div style={{ maxWidth: 640 }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <p style={{ margin: 0, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "rgba(7,10,13,0.4)" }}>
          Back-office
        </p>
        <h1 style={{ margin: "4px 0 0", fontSize: 22, fontWeight: 700, color: "#070a0d", letterSpacing: "-0.02em" }}>
          Recherche utilisateur
        </h1>
      </div>

      {/* Search form */}
      <div style={{
        background: "rgba(255,255,255,0.82)",
        border: "1px solid #fff",
        borderRadius: 16,
        backdropFilter: "blur(10px) saturate(145%)",
        boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
        padding: 20,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}>
        <div>
          <label className="bo-label">Public ID ou Private ID (UUID)</label>
          <input
            className="bo-input"
            type="text"
            placeholder="67734257589719040 ou 550e8400-e29b-..."
            value={identifier}
            onChange={e => setIdentifier(e.target.value)}
            onKeyDown={e => e.key === "Enter" && void search()}
          />
        </div>

        {canBypass && (
          <div>
            <label className="bo-label">Raison (bypass privacy)</label>
            <input
              className="bo-input"
              type="text"
              placeholder="Raison de la consultation…"
              value={reason}
              onChange={e => setReason(e.target.value)}
              onKeyDown={e => e.key === "Enter" && void search()}
            />
          </div>
        )}

        <button
          type="button"
          className="bo-btn"
          disabled={loading || !identifier.trim()}
          onClick={() => void search()}
          style={{ alignSelf: "flex-start" }}
        >
          {loading ? "Recherche…" : "Rechercher"}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          marginTop: 12, padding: "12px 16px",
          borderRadius: 12, background: "rgba(192,57,43,0.08)",
          border: "1px solid rgba(192,57,43,0.18)",
          fontSize: 12, color: "#c0392b",
        }}>
          {error}
        </div>
      )}

      {/* Result */}
      {result && (
        <>
          <div style={{
            marginTop: 16,
            background: "rgba(255,255,255,0.82)",
            border: "1px solid #fff",
            borderRadius: 16,
            backdropFilter: "blur(10px) saturate(145%)",
            boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
            overflow: "hidden",
          }}>
            <div style={{ height: 72, background: accent }} />
            <div style={{ padding: "0 20px 20px", position: "relative" }}>
              <div style={{ position: "absolute", top: -28, left: 20 }}>
                <div style={{ padding: 2.5, borderRadius: 14, backgroundImage: accent }}>
                  <div style={{ padding: 2, borderRadius: 11, background: "rgba(255,255,255,0.95)" }}>
                    <Avatar avatarUrl={result.profile.user.avatarUrl} name={result.profile.user.displayName ?? result.profile.user.username} accent={accent} />
                  </div>
                </div>
              </div>
              <div style={{ height: 28 }} />
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginTop: 6 }}>
                <div>
                  <p style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#070a0d", letterSpacing: "-0.01em" }}>
                    {result.profile.user.displayName ?? result.profile.user.username ?? "Utilisateur Anonyme"}
                  </p>
                  {result.profile.user.username && (
                    <p style={{ margin: "2px 0 0", fontSize: 11, color: "rgba(7,10,13,0.45)", fontFamily: "monospace" }}>
                      @{result.profile.user.username}
                    </p>
                  )}
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <span style={{
                    display: "inline-flex", alignItems: "center", height: 20,
                    borderRadius: 99, padding: "0 8px",
                    fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em",
                    background: result.status === "active" ? "rgba(76,195,138,0.12)" : "rgba(192,57,43,0.10)",
                    color: result.status === "active" ? "#2d7a5a" : "#c0392b",
                    border: `1px solid ${result.status === "active" ? "rgba(76,195,138,0.25)" : "rgba(192,57,43,0.2)"}`,
                  }}>
                    {result.status}
                  </span>
                  {result.subscription && (
                    <span style={{
                      display: "inline-flex", alignItems: "center", height: 20,
                      borderRadius: 99, padding: "0 8px",
                      fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em",
                      background: "rgba(7,10,13,0.06)", color: "rgba(7,10,13,0.5)",
                      border: "1px solid rgba(7,10,13,0.10)",
                    }}>
                      {result.subscription.planCode}
                    </span>
                  )}
                  {result.telegram && (
                    <span style={{
                      display: "inline-flex", alignItems: "center", height: 20,
                      borderRadius: 99, padding: "0 8px", gap: 4,
                      fontSize: 10, fontWeight: 700,
                      background: "rgba(34, 158, 217, 0.12)", color: "#0088cc",
                      border: "1px solid rgba(34, 158, 217, 0.25)",
                    }}>
                      ✈️ {result.telegram.username ? `@${result.telegram.username}` : `TG: ${result.telegram.id}`}
                    </span>
                  )}
                </div>
              </div>
              {result.profile.user.description && (
                <p style={{ margin: "10px 0 0", fontSize: 12, lineHeight: 1.6, color: "rgba(7,10,13,0.6)" }}>
                  {result.profile.user.description}
                </p>
              )}
              <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 5 }}>
                <Row label="Public ID" value={result.profile.user.publicId} mono />
                {result.privateId && <Row label="Private ID" value={result.privateId} mono />}
                <Row label="Membre depuis" value={memberSince!} />
                <Row label="Clé Publique" value={result.publicKey} mono />
                <Row label="Double facteur (2FA)" value={result.totpEnabled ? "Activé (TOTP)" : "Désactivé"} />
                <Row label="Appareils Connectés" value={`${result.connectedDevicesCount} actif(s)`} />
                <Row label="Recherches Historique" value={`${result.savedSearchesCount} recherche(s)`} />
                {result.telegram && (
                  <>
                    <Row label="ID Telegram" value={result.telegram.id} mono />
                    {result.telegram.username && <Row label="Pseudo Telegram" value={`@${result.telegram.username}`} />}
                    {result.telegram.linkedAt && <Row label="Telegram associé" value={new Date(result.telegram.linkedAt).toLocaleDateString("fr-FR")} />}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Solde & Portefeuille */}
          <Section title="💳 Solde & Portefeuille (Wallet)">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10 }}>
              <div style={{
                background: "rgba(76,195,138,0.08)",
                border: "1px solid rgba(76,195,138,0.22)",
                borderRadius: 12,
                padding: "12px 14px",
              }}>
                <span style={{ fontSize: 11, color: "rgba(7,10,13,0.5)", fontWeight: 500 }}>Solde portefeuille</span>
                <div style={{ fontSize: 22, fontWeight: 800, color: "#2d7a5a", marginTop: 2 }}>
                  {result.wallet?.balanceFormatted ?? "0.00 €"}
                </div>
              </div>

              <div style={{
                background: "rgba(7,10,13,0.03)",
                border: "1px solid rgba(7,10,13,0.08)",
                borderRadius: 12,
                padding: "12px 14px",
              }}>
                <span style={{ fontSize: 11, color: "rgba(7,10,13,0.5)", fontWeight: 500 }}>Total rechargé</span>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#070a0d", marginTop: 4 }}>
                  {result.wallet?.totalDepositedFormatted ?? "0.00 €"}
                </div>
              </div>

              <div style={{
                background: "rgba(7,10,13,0.03)",
                border: "1px solid rgba(7,10,13,0.08)",
                borderRadius: 12,
                padding: "12px 14px",
              }}>
                <span style={{ fontSize: 11, color: "rgba(7,10,13,0.5)", fontWeight: 500 }}>Total dépensé</span>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#070a0d", marginTop: 4 }}>
                  {result.wallet?.totalSpentFormatted ?? "0.00 €"}
                </div>
              </div>

              <div style={{
                background: "rgba(7,10,13,0.03)",
                border: "1px solid rgba(7,10,13,0.08)",
                borderRadius: 12,
                padding: "12px 14px",
              }}>
                <span style={{ fontSize: 11, color: "rgba(7,10,13,0.5)", fontWeight: 500 }}>Transactions</span>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#070a0d", marginTop: 4 }}>
                  {result.wallet?.transactionsCount ?? 0}
                </div>
              </div>
            </div>
          </Section>

          {/* Crédits & Usage */}
          <Section title="📊 Usage des Crédits (Rolling 24h)">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                <span style={{ color: "rgba(7,10,13,0.6)" }}>Plan : <strong>{result.credits.plan.code}</strong></span>
                <span style={{ color: result.credits.usagePercent > 80 ? "#c0392b" : "#2d7a5a", fontWeight: "bold" }}>
                  {result.credits.used} / {result.credits.limit ?? "∞"} crédits ({result.credits.usagePercent}%)
                </span>
              </div>
              <div style={{ height: 8, width: "100%", background: "rgba(7,10,13,0.06)", borderRadius: 4, overflow: "hidden" }}>
                <div style={{
                  height: "100%",
                  width: `${Math.min(100, result.credits.usagePercent)}%`,
                  background: result.credits.usagePercent > 80 ? "#c0392b" : "#4cc38a",
                  borderRadius: 4
                }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "rgba(7,10,13,0.4)" }}>
                <span>{result.credits.resetIn ? `Libération dans ${result.credits.resetIn}` : ""}</span>
                <span>Overage : {result.credits.overage.enabled ? "Actif (Wallet)" : "Inactif"}</span>
              </div>
            </div>
          </Section>

          {/* Utilisation sur ces derniers mois */}
          <Section title="📈 Utilisation des derniers mois">
            {Object.keys(result.monthlyUsage).length === 0 ? (
              <p style={{ fontSize: 12, color: "rgba(7,10,13,0.45)", margin: 0 }}>Aucune consommation enregistrée sur les 6 derniers mois.</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(7,10,13,0.1)", textAlign: "left" }}>
                    <th style={{ padding: "4px 8px", color: "rgba(7,10,13,0.5)" }}>Mois</th>
                    <th style={{ padding: "4px 8px", color: "rgba(7,10,13,0.5)" }}>OSINT</th>
                    <th style={{ padding: "4px 8px", color: "rgba(7,10,13,0.5)" }}>CSINT</th>
                    <th style={{ padding: "4px 8px", color: "rgba(7,10,13,0.5)" }}>Blockchain</th>
                    <th style={{ padding: "4px 8px", color: "rgba(7,10,13,0.5)", fontWeight: "bold" }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(result.monthlyUsage).reverse().map(([month, data]) => (
                    <tr key={month} style={{ borderBottom: "1px solid rgba(7,10,13,0.05)" }}>
                      <td style={{ padding: "6px 8px", fontWeight: "bold" }}>{month}</td>
                      <td style={{ padding: "6px 8px" }}>{data.osint} cr.</td>
                      <td style={{ padding: "6px 8px" }}>{data.csint} cr.</td>
                      <td style={{ padding: "6px 8px" }}>{data.blockchain} cr.</td>
                      <td style={{ padding: "6px 8px", fontWeight: "bold", color: "#3498db" }}>{data.total} cr.</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          {/* Historique des abonnements */}
          <Section title="📜 Historique des abonnements">
            {result.subscriptionsList.length === 0 ? (
              <p style={{ fontSize: 12, color: "rgba(7,10,13,0.45)", margin: 0 }}>Aucun abonnement enregistré.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {result.subscriptionsList.map((s) => {
                  const isActive = s.status === "active";
                  const startStr = new Date(s.currentPeriodStart).toLocaleDateString("fr-FR");
                  const endStr = new Date(s.currentPeriodEnd).toLocaleDateString("fr-FR");
                  return (
                    <div key={s.id} style={{
                      padding: 12,
                      borderRadius: 12,
                      background: isActive ? "rgba(76,195,138,0.05)" : "rgba(7,10,13,0.02)",
                      border: `1px solid ${isActive ? "rgba(76,195,138,0.2)" : "rgba(7,10,13,0.06)"}`,
                      display: "flex",
                      flexDirection: "column",
                      gap: 4
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#070a0d" }}>
                          {s.planCode.toUpperCase()}
                        </span>
                        <span style={{
                          fontSize: 9,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          padding: "2px 6px",
                          borderRadius: 6,
                          background: isActive ? "rgba(76,195,138,0.15)" : "rgba(7,10,13,0.08)",
                          color: isActive ? "#2d7a5a" : "rgba(7,10,13,0.5)"
                        }}>
                          {s.status}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: "rgba(7,10,13,0.5)" }}>
                        Période : {startStr} — {endStr}
                      </div>
                      <div style={{ fontSize: 10, color: "rgba(7,10,13,0.4)" }}>
                        Créé le : {new Date(s.createdAt).toLocaleString("fr-FR")}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

          {/* Ban / Unban */}
          {canBypass && (
            <Section title={result.status === "active" ? "🚫 Bannir l'utilisateur" : "✅ Débannir l'utilisateur"}>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div>
                  <label className="bo-label">Raison</label>
                  <input className="bo-input" type="text" placeholder="Raison du ban…"
                    value={banReason} onChange={e => setBanReason(e.target.value)} />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {result.status === "active" ? (
                    <button className="bo-btn" disabled={banLoading}
                      onClick={() => void handleBan("suspended")}
                      style={{ background: "rgba(192,57,43,0.1)", color: "#c0392b", border: "1px solid rgba(192,57,43,0.2)" }}>
                      {banLoading ? "…" : "Bannir"}
                    </button>
                  ) : (
                    <button className="bo-btn" disabled={banLoading}
                      onClick={() => void handleBan("active")}
                      style={{ background: "rgba(76,195,138,0.1)", color: "#2d7a5a", border: "1px solid rgba(76,195,138,0.25)" }}>
                      {banLoading ? "…" : "Débannir"}
                    </button>
                  )}
                </div>
                {banMsg && (
                  <p style={{ margin: 0, fontSize: 11, color: banMsg.startsWith("✓") ? "#2d7a5a" : "#c0392b" }}>
                    {banMsg}
                  </p>
                )}
              </div>
            </Section>
          )}


          {/* Send Reset Email */}
          <Section title="✉️ Envoyer un lien de récupération">
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                <label className="bo-label">Adresse email fournie par l'utilisateur</label>
                <input className="bo-input" type="email" placeholder="Email pour recevoir le lien..."
                  value={resetEmail} onChange={e => setResetEmail(e.target.value)} />
              </div>
              <button className="bo-btn" disabled={resetLoading || !resetEmail} onClick={() => void handleSendReset()}
                style={{ alignSelf: "flex-start", background: "rgba(52,152,219,0.1)", color: "#2980b9", border: "1px solid rgba(52,152,219,0.25)" }}>
                {resetLoading ? "Envoi..." : "Envoyer le lien"}
              </button>
              {resetMsg && (
                <p style={{ margin: 0, fontSize: 11, color: resetMsg.startsWith("✓") ? "#2d7a5a" : "#c0392b" }}>
                  {resetMsg}
                </p>
              )}
            </div>
          </Section>

          {/* Grant subscription */}
          <Section title="🎁 Accorder un abonnement">
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", gap: 10 }}>

                <div style={{ flex: 1 }}>
                  <label className="bo-label">Tier produit</label>
                  <select className="bo-input" value={grantTier} onChange={e => setGrantTier(e.target.value as Tier)}>
                    {TIER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label className="bo-label">Durée</label>
                  <select className="bo-input" value={grantDays} onChange={e => setGrantDays(Number(e.target.value))}>
                    {DURATION_OPTIONS.map(o => <option key={o.days} value={o.days}>{o.label}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label className="bo-label">Raison</label>
                  <input className="bo-input" type="text" placeholder="Raison…"
                    value={grantReason} onChange={e => setGrantReason(e.target.value)} />
                </div>
              </div>
              <button className="bo-btn" disabled={grantLoading} onClick={() => void handleGrant()}
                style={{ alignSelf: "flex-start" }}>
                {grantLoading ? "…" : "Accorder l'abonnement"}
              </button>
              {grantMsg && (
                <p style={{ margin: 0, fontSize: 11, color: grantMsg.startsWith("✓") ? "#2d7a5a" : "#c0392b" }}>
                  {grantMsg}
                </p>
              )}
            </div>
          </Section>
        </>
      )}
    </div>
  );
}
